import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { GetPointResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { editPointUpvote } from '../helpers/api/points-api';
import { electionNominationsAreHappening } from '../helpers/api/election-api';
import { getCookie } from '../helpers/device';
import { getMainPage } from '../helpers/page-name';
import { isDefined } from '../helpers/utils';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('nomination-component')
export class NominationComponent extends LitElement {
    amplitude_callsite: string = 'Nomination Component';

    @property({ type: Boolean })
    isInMap = false;

    @state()
    step: string = 'select_category';

    @state()
    selected_category_id: string | undefined;

    @state()
    focused_point_id: string | undefined;

    @state()
    user_id_cookie = getCookie('user_id');

    @state()
    votingCategories = topicData.topic?.categories ?? [];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            false // using cache here can cause issues as users upvote places. one day if we can fix the cache we can turn this on
        );
        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.allCheckins
            );
        }

        const excludedCategoryIds = topicData.electionHappeningNow?.excluded_categories?.map((ec) => ec.$oid) ?? [];
        this.votingCategories = topicData.categories.filter((c) => c._id && !excludedCategoryIds.includes(c._id.$oid));
    }

    setFocusedPoint(point_id: string) {
        this.focused_point_id = point_id;
        const point = pointCache.getPoint(point_id);
        if (point?.topic_category_id?.$oid) {
            this.selected_category_id = point.topic_category_id.$oid;
            this.step = 'existing_nominees';
        }
    }

    private async openThisMap() {
        // get topic key
        const queryString = window.location.href.split('?')[1];
        const params = new URLSearchParams(queryString);
        const topicKey = params.get('topic_key');

        // open URL
        const openURL = `${window.location.origin}/#command-center?topic_id=${topicData.topicId}&topic_key=${topicKey ?? ''}`;
        window.open(openURL, '_blank');
    }

    get title() {
        if (this.step === 'select_category') {
            return 'Choose a Category to See Nominees';
        } else if (this.step === 'add_point') {
            return 'Add a Nominee';
        } else if (this.step === 'existing_nominees') {
            const category_name = topicData.categories.find((c) => c._id?.$oid === this.selected_category_id)?.name ?? 'Category';
            return `Nominees for ${category_name}`;
        } else {
            return 'Nomination';
        }
    }

    get subtitle() {
        if (this.step === 'select_category') {
            return '';
        } else if (this.step === 'add_point') {
            return '';
        } else if (this.step === 'existing_nominees') {
            return 'Upvote your favorite nominees to help them become finalists!';
        } else {
            return '';
        }
    }

    private async toggleUpvote(point_id: string) {
        const point = pointCache.getPoint(point_id);
        if (point && this.getHasThisUserUpvoted(point)) {
            await this.editUpvotePoint(point);

            logWithAmplitude(this.amplitude_callsite, 'Remove Upvote Click', {
                point_id,
                topic_id: topicData.topicId,
            });
        } else {
            await this.editUpvotePoint(point);

            logWithAmplitude(this.amplitude_callsite, 'Upvote Click', {
                point_id,
                topic_id: topicData.topicId,
            });
        }
        this.requestUpdate();
    }

    getHasThisUserUpvoted(point: GetPointResponse) {
        return point?.likes?.includes(this.user_id_cookie ?? '') ?? false;
    }

    private async editUpvotePoint(point?: GetPointResponse) {
        const userString = getCookie('user_id');

        if (isDefined(point) && userString) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(point)) {
                    await editPointUpvote(
                        this.amplitude_callsite,
                        topicData.topicId,
                        point._id.$oid,
                        topicData.topicKey
                    );

                    await pointCache.refreshPoint(point._id.$oid);
                }
            }, `An error has occurred.  Context: could not upvote the point.`);
        }
    }


    render() {
        const nominationsLive = topicData.electionHappeningNow ? electionNominationsAreHappening(topicData.electionHappeningNow) : false;
        if (!nominationsLive && !topicData.isWriter) {
            return this.renderNominationsNotLive();
        }
        return html`
                <div class="points__content_wrapper">
                    ${topicData.isWriter && !nominationsLive ? html`
                        <p style="text-align: center; background-color: var(--main-invalid-color);">
                            Nominations are closed but you are seeing this page because you are an admin
                        </p>
                    ` : ''}
                    ${this.isInMap ? html`
                        ` : html`
                        <div class="points__header">
                            <map-header>
                                ${this.step !== 'select_category' ? html`
                                    <form-button size="sm" class="points__view_map" variant="custom" style="--btn-border-color:${topicData.topic?.header_text_color};--btn-label-color:${topicData.topic?.header_text_color};--btn-background-color:transparent;" @click="${() => {this.step = 'select_category';}}">
                                        Back
                                    </form-button>
                                ` : ''}
                            </map-header>
                        </div>
                    `}
                    <div class="points__middle_content">
                        <div class="points__add_point_section">
                            <div class="points__add_point_header">
                                <h2>${this.title}</h2>
                                ${this.subtitle ? html`<p>${this.subtitle}</p>` : ''}
                            </div>
                            ${this.renderByStep()}
                        </div>
                        <div class="nomination_actions">
                            ${this.step !== 'select_category' && this.isInMap ? html`
                                <form-button size="sm" variant="outline" class="points__view_map" @click="${() => {this.step = 'select_category';}}">
                                    Back
                                </form-button>
                            ` : ''}
                            ${this.step !== 'add_point' ? html`
                                <form-button
                                    class="add_nominee_button"
                                    size="lg"
                                    @click="${() => {
                                        this.step = 'add_point';
                                    }}"
                                >${this.step !== 'existing_nominees' ? 'Add Nominee' : `Add ${topicData.categories.find((c) => c._id?.$oid === this.selected_category_id)?.name ?? ''} Nominee`}
                                </form-button>
                            ` : ''}
                        </div>
                    </div>
                </div>
            <error-toast id="errorToast"></error-toast>
        `;


    }

    renderByStep() {
         if (topicData.isReady) {
            switch (this.step) {
                case 'select_category':
                    return this.renderSelectCategory();
                case 'add_point':
                    return this.renderAddPoint(this.selected_category_id);
                case 'existing_nominees':
                    return this.renderExistingNominees(this.selected_category_id, this.focused_point_id);
                default:
                    return this.renderSelectCategory();
            }
        }
    }

    renderNominationsNotLive() {
        return html`
            <div class="points__page_wrapper">
                <div class="points__content_wrapper" style="text-align: center;">
                    <h2>Nominations are closed</h2>
                    <p>Check back soon!</p>
                </div>
            </div>
        `;
    }

    renderExistingNominees(category_id?: string, point_id?: string) {
        const nominees = pointCache.allPoints.filter((p) => p.topic_category_id?.$oid === category_id);
        // sort by if this user has upvoted
        nominees.sort((a, b) => {
            if (this.getHasThisUserUpvoted(a) && !this.getHasThisUserUpvoted(b)) {
                return -1;
            } else if (!this.getHasThisUserUpvoted(a) && this.getHasThisUserUpvoted(b)) {
                return 1;
            } else {
                return 0;
            }
        });

        // move sponsors/featured to top
        nominees.sort((a, b) => {
            if (a.featured && !b.featured) {
                return -1;
            } else if (!a.featured && b.featured) {
                return 1;
            } else {
                return 0;
            }
        });

        // move point_id to top if defined
        let pointIndex = -1;
        if (point_id) {
            pointIndex = nominees.findIndex((p) => p._id.$oid === point_id);
        }
        if (pointIndex >= 0) {
            const [point] = nominees.splice(pointIndex, 1);
            nominees.unshift(point);
        }
        return html`
            <div class="existing_nominees_wrapper">
                ${this.votingCategories.filter((c) => c._id?.$oid === category_id).map((c) => {
                    if (!c.description) {
                        return html``;
                    }
                    return html`
                        <h2>${c.description}</h2>
                    `;
                })}
                ${nominees.map((point) => {
                    return html`
                        <div class="existing_nominee ${point._id.$oid === this.focused_point_id ? 'highlighted' : ''}" @click="${async () => await this.toggleUpvote(point._id.$oid)}">
                            ${point.likes?.includes(this.user_id_cookie ?? '') ?? false ?
                                html`<mwc-icon>thumb_up</mwc-icon>` :
                                html`<mwc-icon>thumb_up_off_alt</mwc-icon>`
                            }
                            <p class="nominee_text">${point.name}${point.likes?.includes(this.user_id_cookie ?? '') ?? false ? ' (Upvoted!)' : ''}</p>
                        </div>
                    `;
                })}
            </div>
        `;
    }

    renderSelectCategory() {
        return html`
        <div class="select-category-wrapper">
                ${this.votingCategories.map((c) => html`
                    <div class="select-category-option" @click="${() => this.goToCategoryNominees(c._id?.$oid)}">
                        <div class="icon-wrapper">
                            <img src="${c.icon}" alt="${c.name}" style="width: 20px; height: 20px; object-fit: contain;">
                        </div>
                        <span>${c.name}</span>
                    </div>
                `)}
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    goToCategoryNominees(category_id?: string) {
        if (!category_id) {
            return;
        }
        this.selected_category_id = category_id;
        this.step = 'existing_nominees';
    }

    renderAddPoint(category_id?: string) {
        return  html`
            <add-edit-point-component
                isNominationPage
                nomination_category_id="${category_id}"
                .editMode=${false}
                hideTitle
                style="flex: 1; overflow-y: auto;"
                modalHeight="70vh"
                @close-point-modal="${async () => {
                    this.step = 'select_category';
                }}"
                @go-to-upvote-point="${async (e: CustomEvent) => {
                    this.goToCategoryNominees(e.detail.point.topic_category_id?.$oid);
                }}"
            >
            </add-edit-point-component>
            <error-toast id="errorToast"></error-toast> `;
    }

    static get styles() {
        return css`
            .points__page_wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 50px 15%;
            }

            .points__content_wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 1200px;
                justify-content: center;
                background-color: white;
                margin-bottom: 20px;
                height: 100%;
            }

            .points__middle_content {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: top;
                gap: 10px;
                flex: 1;
                overflow-y: auto;
            }

            .points__add_point_section {
                flex: 1 1 65%;
                padding-left: 10px;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
            }

            .points__add_point_header {
                text-align: center;
            }

            p {
                margin-top: 5px;
                margin-bottom: 0;
            }

            h2 {
                margin-bottom: 10px;
            }

            .add_nominee_button {
                animation: glow 1s alternate;
                animation-duration: 1s; /* Duration of one iteration */
                animation-iteration-count: 5; /* Number of iterations */
                animation-fill-mode: forwards;
            }

            @keyframes glow {
                from {
                    box-shadow: 0 0 5px -5px --var(main-featured-color);
                }
                to {
                    box-shadow: 0 0 5px 5px var(--main-featured-color);
                }
            }

            .inline_words {
                display: inline-flex;
                flex-direction: row;
                align-items: flex-end;
                justify-content: flex-start;
                gap: 5px;
            }

            .inline_words p {
                margin-bottom: 5px;
            }

            .inline_words h2 {
                margin-top: 0;
            }

            .points__upgrade_section_items {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                margin-top: 10px;
            }

            .points__upgrade_button {
                --btn-background-color: var(--main-secondary-color);
                margin-top: 10px;
            }

            .select-category-option{
                width: calc(25% - 20px);
                display: flex;
                flex-direction: column;
                background-color: var(--theme-border-color);
                box-shadow: 0 0 5px 0 rgba(0,0,0,0.1);
                cursor: pointer;
                padding: 5px;
                align-items: center;
                justify-content: center;
                line-height: 1;
                text-align: center;
            }

            .existing_nominees_wrapper {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                margin-top: 10px;
            }

            .existing_nominee {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                gap: 10px;
                height: 25px;
                border: 1px solid var(--theme-border-color);
                border-radius: 5px;
                cursor: pointer;
            }

            .nominee_text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .highlighted {
                background-color: var(--main-sky-color);
                margin-bottom: 5px;
            }

            .points__upgrade_section_item {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                padding: 10px;
                border-radius: 10px;
            }

            .info_section {
                border: .5px solid var(--main-primary-color);
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
            }

            .select-category-option:hover {
                background-color: var(--main-sky-color);
            }

            .nomination_actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 10px;
                flex-wrap: wrap;
            }

            .map_views_section {
                background-color: var(--main-primary-color);
                color: white;
                text-color: white;
            }

            .upgrade_section {
                border: .5px solid var(--main-primary-color);
            }

            .sparkles {
                height: 20px;
            }

            .hide_on_desktop {
                display: none;
            }

            .select-category-wrapper {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                width: 100%;
                align-items: stretch;
                justify-content: flex-start;
                gap: 10px;
                overflow-y: auto;
                font-size: 0.675rem;
            }

            @media only screen and (max-width: 550px) {
                .points__page_wrapper {
                    padding: 0;
                }

                .points__middle_content {
                    flex-direction: column;
                    align-items: center;
                    padding: 10px;
                }

                .points__upgrade_section {
                    padding-right: 0;
                    border-right: none;
                }

                .hide_on_mobile {
                    display: none;
                }

                .info_section {
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border: .5px solid var(--main-primary-color);
                    gap: 5px;
                    text-align: center;
                }

                .hide_on_desktop {
                    display: flex;
                }
                .select-category-option{
                    width: calc(50% - 20px);
                }

            }



        `;
    }
}
